import { ReactElement, useEffect, useMemo, useState } from 'react'
import {
  create as createNiceModal,
  useModal,
  show as showModal,
} from '@ebay/nice-modal-react'
import BaseDrawer from 'src/ui-components/modals/BaseDrawer'
import PartSummary from '../Common/PartSummary'
import ValidationMessage from '../../Messages/ValidationMessage'
import { EditablePart } from '../Common/interfaces'
import { ValidationTag } from '../../Common/interfaces'
import EditPartDrawer from '..'
import styled from 'styled-components'
import { Button, Checkbox } from 'src/ui-components'
import { observer } from 'mobx-react-lite'
import { AddToListStore } from 'src/store/lists/AddToListStore'
import { StoreInstances } from 'src/store/StoreInstancesContainer'
import { I18n } from 'src/i18n'
import { ICheckboxClickHandler } from 'src/ui-components/forms/Checkbox'

interface Props {
  listId: number
  group: string
  oldPart: EditablePart
  newParts: EditablePart[]
  store: AddToListStore
}

const ReviewPartDrawer = observer(
  createNiceModal((props: Props): ReactElement => {
    const { listId, group, newParts, oldPart, store } = props
    const [parts, setParts] = useState(Array<EditablePart>)

    useEffect(() => {
      setParts(newParts)
    }, [newParts])

    const validationTag = newParts?.[0]?.validationTag

    const modal = useModal()
    const [isSavingChanges, setIsSavingChanges] = useState(false)

    const showButtons = useMemo(
      () => validationTag == ValidationTag.INVALID,
      [validationTag]
    )

    const handleSelectPart = (e: ICheckboxClickHandler) => {
      const { value: seqno } = e
      const idx = parts.findIndex((p) => p?.seqno == seqno)
      if (idx >= 0) {
        const part = parts[idx]
        parts[idx] = { ...part, isSelected: !part?.isSelected }
        setParts([...parts])
      }
    }

    const handleEdit = (part: EditablePart): void => {
      showModal(EditPartDrawer, { listId, group, part, store })
    }

    const handleRemove = (): void => {
      modal.remove()
      store.removePart(oldPart.seqno)
    }

    const handleSave = async (): Promise<void> => {
      try {
        setIsSavingChanges(true)

        /** Duplicates result in more than one part from which we want to add only the selected ones */
        const selectedParts =
          parts.length == 1 ? parts : parts.filter((p) => p.isSelected)

        await store.updateParts(
          oldPart.seqno,
          selectedParts.map((part) => ({ ...part, seqno: oldPart.seqno }))
        )
        StoreInstances.uiStore.displaySuccessNotification(
          'itemSuccessfullyUpdated'
        )
        modal.remove()
      } finally {
        setIsSavingChanges(false)
      }
    }

    const handleDiscard = (): void => {
      showModal(EditPartDrawer, { listId, group, part: oldPart, store })
    }

    const body = (
      <div>
        <ValidationMessage type={validationTag} />
        {validationTag == ValidationTag.DUPLICATE && (
          <SelectInstructions>
            <I18n name="pleaseSelectTheItemsYouWishToInclude" />.
          </SelectInstructions>
        )}
        {parts?.map((p, idx) => (
          <PartSummaryWrapper key={`review_item_${idx}`}>
            {validationTag == ValidationTag.DUPLICATE && (
              <CheckboxWrapper>
                <Checkbox
                  label=""
                  value={p.seqno}
                  onClick={handleSelectPart}
                  active={p.isSelected}
                />
              </CheckboxWrapper>
            )}

            <PartSummary
              part={p}
              showButtons={showButtons}
              onEdit={handleEdit}
              onRemove={handleRemove}
            />
          </PartSummaryWrapper>
        ))}
        <ButtonsContainer>
          <Button
            type="reset"
            text="discardChanges"
            variant="secondary"
            onClick={handleDiscard}
          />
          <Button
            type="submit"
            text="saveChanges"
            onClick={handleSave}
            loading={isSavingChanges}
          />
        </ButtonsContainer>
      </div>
    )

    return (
      <BaseDrawer
        title={'reviewItem'}
        body={body}
        open={modal.visible}
        onClose={modal.remove}
        width="544px"
      />
    )
  })
)

const Flex = styled.div`
  display: flex;
`

const ButtonsContainer = styled(Flex)`
  margin-top: 24px;
  justify-content: end;
  gap: 16px;
`

const PartSummaryWrapper = styled.div`
  display: flex;
`

const SelectInstructions = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  text-align: left;
  margin: 16px 0px;
`

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 0px 24px 32px 0px;
`

export default ReviewPartDrawer
